import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, FileUp, FileText, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { useTranslation } from "react-i18next";

const allowedBranchIds = (sup) => {
  if (!sup) return [];
  const fromOps = (sup.branch_operations || []).filter((o) => o.active !== false).map((o) => o.branch_id);
  return Array.from(new Set([...fromOps, ...(sup.branches_authorized || [])].filter(Boolean)));
};

export default function InvoiceUploadModal({ po = null, onClose, onSuccess }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const noPo = !po;
  const [pdfFile, setPdfFile] = useState(null);
  const [xmlFile, setXmlFile] = useState(null);
  const [xmlData, setXmlData] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [supplierId, setSupplierId] = useState("");
  const [branchId, setBranchId] = useState("");

  useEffect(() => {
    if (!noPo) return;
    (async () => {
      try {
        const [s, b] = await Promise.all([api.get("/suppliers"), api.get("/branches")]);
        const sups = (s.data || []).filter((x) => !x.archived_at);
        setSuppliers(sups);
        setBranches(b.data || []);
        if (user?.role === "supplier") setSupplierId(user.supplier_id || sups[0]?.id || "");
      } catch {
        toast.error(t("common.error"));
      }
    })();
  }, [noPo]);

  const selectedSupplier = useMemo(
    () => suppliers.find((s) => s.id === supplierId) || null,
    [suppliers, supplierId]
  );

  const branchOptions = useMemo(() => {
    const ids = allowedBranchIds(selectedSupplier);
    return branches.filter((b) => ids.includes(b.id));
  }, [branches, selectedSupplier]);

  useEffect(() => {
    if (noPo && branchOptions.length === 1) setBranchId(branchOptions[0].id);
  }, [branchOptions.length, noPo]);

  const parseXml = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const parser = new DOMParser();
        const xml = parser.parseFromString(e.target.result, "text/xml");
        const tfd = xml.getElementsByTagName("tfd:TimbreFiscalDigital")[0];
        const uuid = tfd?.getAttribute("UUID") || "";
        const total = parseFloat(xml.documentElement.getAttribute("Total") || "0");
        // Fix WH68 (2026-10-09): el precio se compara sin IVA, como en el servidor.
        const subtotalAttr = xml.documentElement.getAttribute("SubTotal");
        const subtotal = subtotalAttr != null ? parseFloat(subtotalAttr) : null;
        const folio = xml.documentElement.getAttribute("Folio") || "";
        const fecha = xml.documentElement.getAttribute("Fecha") || "";
        const emisor = xml.getElementsByTagName("cfdi:Emisor")[0] || xml.getElementsByTagName("Emisor")[0];
        const rfc = emisor?.getAttribute("Rfc") || "";
        setXmlData({ uuid, total, subtotal, folio, fecha, rfc });
      } catch (err) {
        toast.error("Error al parsear XML CFDI");
      }
    };
    reader.readAsText(file);
  };

  const handleXml = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setXmlFile(f);
    parseXml(f);
  };

  const upload = async () => {
    if (!pdfFile || !xmlFile) {
      toast.error(t("invoiceModal.filesRequired"));
      return;
    }
    if (noPo && user?.role !== "supplier" && !supplierId) {
      toast.error(t("invoiceModal.supplierRequired"));
      return;
    }
    if (noPo && !branchId) {
      toast.error(t("invoiceModal.branchRequired"));
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("pdf", pdfFile);
      fd.append("xml", xmlFile);
      if (noPo) {
        fd.append("branch_id", branchId);
        if (user?.role !== "supplier") fd.append("supplier_id", supplierId);
      } else {
        fd.append("po_id", po.id);
      }
      await api.post("/invoices/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("invoiceModal.uploaded"));
      onSuccess?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("invoiceModal.uploadError"));
    } finally {
      setUploading(false);
    }
  };

  const totalsMatch = !noPo && xmlData ? Math.abs(xmlData.total - po.total) < 0.01 : null;
  // Fix WH68 (2026-10-09): misma regla que el servidor (hasta $1.00 o 0.5%) sobre
  // el subtotal sin IVA. Si coincide, la factura no queda con discrepancia de precio.
  const subtotalMatches = !noPo && xmlData && xmlData.subtotal != null && po.subtotal != null
    ? (() => {
        const d = Math.abs(xmlData.subtotal - po.subtotal);
        return d <= 1 || (po.subtotal ? (d / po.subtotal) * 100 <= 0.5 : true);
      })()
    : null;
  const canSubmit = !!pdfFile && !!xmlFile && (!noPo ? true : !!branchId && (user?.role === "supplier" || !!supplierId));

  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
        <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
          data-testid={noPo ? "invoice-upload-modal-no-po" : "invoice-upload-modal"}
          className="bg-white rounded-2xl w-full max-w-xl overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="w-10 h-10 rounded-xl bg-[#FFDF00] flex items-center justify-center">
                <FileUp className="w-5 h-5 text-[#1A1A1A]" />
              </div>
              <div>
                <h2 className="font-display font-bold text-lg">
                  {noPo ? t("invoiceModal.titleNoPo") : t("invoiceModal.title", { folio: po.folio })}
                </h2>
                <p className="text-xs text-slate-500">{noPo ? t("invoiceModal.subtitleNoPo") : t("invoiceModal.subtitle")}</p>
              </div>
            </div>
            <button onClick={onClose} data-testid="inv-upload-close-btn" className="w-9 h-9 rounded-xl hover:bg-slate-100 flex items-center justify-center">
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="p-6 space-y-4">
            {noPo && user?.role !== "supplier" && (
              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1">{t("invoiceModal.selectSupplier")}</label>
                <select data-testid="inv-supplier-select" value={supplierId}
                  onChange={(e) => { setSupplierId(e.target.value); setBranchId(""); }}
                  className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]">
                  <option value="">{t("invoiceModal.selectSupplierPh")}</option>
                  {suppliers.map((s) => (
                    <option key={s.id} value={s.id}>{s.business_name}{s.rfc ? ` — ${s.rfc}` : ""}</option>
                  ))}
                </select>
              </div>
            )}

            {noPo && selectedSupplier && (
              <div data-testid="inv-on-behalf-of" className="bg-[#FFF9DB] border border-[#FFDF00] rounded-xl p-3">
                <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{t("invoiceModal.onBehalfOf")}</div>
                <div className="font-display font-bold text-sm text-[#1A1A1A] mt-0.5">{selectedSupplier.business_name}</div>
                <div className="text-xs text-slate-600 font-mono">{t("invoiceModal.rfcLabel")}: {selectedSupplier.rfc || "—"}</div>
                <div className="text-[11px] text-slate-500 mt-1">{t("invoiceModal.rfcWarning")}</div>
              </div>
            )}

            {noPo && (
              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1">{t("invoiceModal.selectBranch")}</label>
                <select data-testid="inv-branch-select" value={branchId} onChange={(e) => setBranchId(e.target.value)}
                  className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]">
                  <option value="">{t("invoiceModal.selectBranchPh")}</option>
                  {branchOptions.map((b) => (
                    <option key={b.id} value={b.id}>{b.name}</option>
                  ))}
                </select>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <input id="pdf-upload" data-testid="inv-pdf-upload" type="file" accept=".pdf"
                  onChange={(e) => setPdfFile(e.target.files[0])} className="hidden" />
                <label htmlFor="pdf-upload" className={`flex flex-col items-center justify-center gap-2 px-4 py-6 rounded-xl border-2 border-dashed cursor-pointer transition ${
                  pdfFile ? "border-emerald-400 bg-emerald-50" : "border-slate-300 hover:border-[#FFDF00]"
                }`}>
                  <FileText className={`w-7 h-7 ${pdfFile ? "text-emerald-600" : "text-slate-400"}`} />
                  <div className="text-center">
                    <div className="text-xs font-bold">{pdfFile ? pdfFile.name : t("invoiceModal.uploadPdf")}</div>
                    <div className="text-[10px] text-slate-500 mt-0.5">{t("invoiceModal.pdfDesc")}</div>
                  </div>
                </label>
              </div>
              <div>
                <input id="xml-upload" data-testid="inv-xml-upload" type="file" accept=".xml"
                  onChange={handleXml} className="hidden" />
                <label htmlFor="xml-upload" className={`flex flex-col items-center justify-center gap-2 px-4 py-6 rounded-xl border-2 border-dashed cursor-pointer transition ${
                  xmlFile ? "border-emerald-400 bg-emerald-50" : "border-slate-300 hover:border-[#FFDF00]"
                }`}>
                  <FileText className={`w-7 h-7 ${xmlFile ? "text-emerald-600" : "text-slate-400"}`} />
                  <div className="text-center">
                    <div className="text-xs font-bold">{xmlFile ? xmlFile.name : t("invoiceModal.uploadXml")}</div>
                    <div className="text-[10px] text-slate-500 mt-0.5">{t("invoiceModal.xmlDesc")}</div>
                  </div>
                </label>
              </div>
            </div>

            {xmlData && (
              <div className="bg-slate-50 rounded-xl p-4 space-y-2 text-xs">
                <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">{t("invoiceModal.autoExtracted")}</div>
                <div className="flex justify-between"><span>{t("invoiceModal.uuid")}</span><span className="font-mono text-[10px]">{xmlData.uuid}</span></div>
                <div className="flex justify-between"><span>{t("invoiceModal.fiscalFolio")}</span><span className="font-mono">{xmlData.folio}</span></div>
                {xmlData.rfc && (
                  <div className="flex justify-between"><span>{t("invoiceModal.issuerRfc")}</span><span className="font-mono">{xmlData.rfc}</span></div>
                )}
                <div className="flex justify-between"><span>{t("invoiceModal.issuedAt")}</span><span>{xmlData.fecha?.split("T")[0]}</span></div>
                <div className="flex justify-between font-bold pt-1 border-t border-slate-200">
                  <span>{t("invoiceModal.total")}</span>
                  <span className="font-mono">${xmlData.total.toFixed(2)}</span>
                </div>
                {totalsMatch !== null && (
                  <div className={`mt-2 pt-2 border-t border-slate-200 flex items-center gap-2 text-xs ${totalsMatch || subtotalMatches ? "text-emerald-700" : "text-amber-700"}`}>
                    {totalsMatch || subtotalMatches ? <CheckCircle2 className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />}
                    <span className="font-bold">{t("invoiceModal.autoValidation")}</span>
                    {totalsMatch
                      ? t("invoiceModal.matchesPo")
                      : subtotalMatches
                        ? t("invoiceModal.subtotalMatchesPo", { diff: (xmlData.total - po.total).toFixed(2) })
                        : t("invoiceModal.diffFromPo", { diff: (xmlData.total - po.total).toFixed(2) })}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex items-center justify-end gap-2">
            <button onClick={onClose} className="px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm font-semibold hover:bg-slate-100">
              {t("common.cancel")}
            </button>
            <button data-testid="inv-upload-submit-btn" onClick={upload} disabled={uploading || !canSubmit}
              className="btn-primary-chizy px-5 py-2 rounded-xl text-sm flex items-center gap-1.5 disabled:opacity-50">
              <FileUp className="w-4 h-4" /> {uploading ? t("invoiceModal.uploading") : t("invoiceModal.upload")}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
